import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Menu, MenuItem, MenuDivider } from '@blueprintjs/core';

export interface MentionListProps {
  items: any[];
  command: (item: any) => void;
}

export const MentionList = forwardRef<any, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Group items by type
  const groupedItems = props.items.reduce((acc: any, item: any) => {
    const type = item.type || 'other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(item);
    return acc;
  }, {});

  const categories = Object.keys(groupedItems);
  const hasMultipleCategories = categories.length > 1;

  const selectItem = (item: any) => {
    // Don't select disabled hint items
    if (item && !item.disabled) {
      props.command({
        id: item.id,
        label: item.label,
        type: item.type,
        metadata: item.metadata,
      });
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    const item = props.items[selectedIndex];
    selectItem(item);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);
  useEffect(() => {
    // Auto-expand first category if items exist
    if (categories.length > 0 && !expandedCategory) {
      setExpandedCategory(categories[0]);
    }
  }, [categories]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  const getCategoryIcon = (type: string) => {
    switch (type) {
      case 'mindmap': return '🗺️';
      case 'node': return '📍';
      case 'user': return '👤';
      case 'organization': return '🏢';
      default: return '•';
    }
  };

  const getCategoryLabel = (type: string) => {
    switch (type) {
      case 'mindmap': return 'Mindmaps';
      case 'node': return 'Nodes';
      case 'user': return 'Users';
      case 'organization': return 'Organizations';
      default: return type;
    }
  };

  return (
    <Menu
      style={{
        backgroundColor: 'white',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        maxHeight: '320px',
        overflowY: 'auto',
        minWidth: '280px',
        maxWidth: '400px',
      }}
    >
      {props.items.length > 0 ? (
        hasMultipleCategories ? (
          // Hierarchical view with categories
          categories.map((category, catIdx) => (
            <React.Fragment key={category}>
              {catIdx > 0 && <MenuDivider />}
              <MenuItem
                icon={
                  <span style={{ fontSize: '14px', marginRight: '4px' }}>
                    {getCategoryIcon(category)}
                  </span>
                }
                text={getCategoryLabel(category)}
                labelElement={
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                    {groupedItems[category].length}
                  </span>
                }
                onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                active={expandedCategory === category}
              />
              {expandedCategory === category && groupedItems[category].map((item: any, idx: number) => {
                const globalIndex = props.items.indexOf(item);
                return (
                  <MenuItem
                    key={item.id || idx}
                    icon={
                      <span style={{ fontSize: '14px', marginRight: '4px' }}>
                        {item.icon}
                      </span>
                    }
                    text={item.label}
                    labelElement={
                      item.subtitle ? (
                        <span style={{ fontSize: '11px', color: '#6b7280' }}>
                          {item.subtitle}
                        </span>
                      ) : undefined
                    }
                    onClick={() => selectItem(item)}
                    active={globalIndex === selectedIndex}
                    style={{
                      paddingLeft: '32px',
                      fontSize: '13px',
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))
        ) : (
          // Flat view if only one category
          props.items.map((item, index) => (
            <MenuItem
              key={item.id || index}
              icon={
                <span style={{ fontSize: '14px', marginRight: '4px' }}>
                  {item.icon}
                </span>
              }
              text={item.label}
              labelElement={
                item.subtitle ? (
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                    {item.subtitle}
                  </span>
                ) : undefined
              }
              onClick={() => selectItem(item)}
              active={index === selectedIndex}
            />
          ))
        )
      ) : (
        <MenuItem text="No results" disabled />
      )}
    </Menu>
  );
});

MentionList.displayName = 'MentionList';





